import { CampaignCsvExportCard } from './CampaignCsvExportCard';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import GlowingOrb, { type OrbForm } from '../GlowingOrb';
import { Avatar } from './Avatar';
import { AskMarkdown, askSlot, stripSlotMarkers } from './AskMarkdown';
import { useSpeechDictation } from '../../lib/useSpeechDictation';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import type { Citation, AskToolCall, AskActivity } from '../../services/geminiService';

// Chart cards carry the chart library, so they load only when an answer has one.
const AskChartCard = React.lazy(() => import('./analytics/AskChartCard'));

/** One turn in the ask-the-campaign chat thread. */
export interface ChatMessage {
  /** Stable id for keying and streaming updates. */
  id: string;
  /** Who produced the turn. */
  role: 'user' | 'assistant';
  /** The turn text. Assistant text may carry inline `[C<id>:T<idx>]` labels. */
  content: string;
  /** Grounded citations for an assistant turn (empty until they arrive). */
  citations: Citation[];
  /** Optional provider reasoning retained with the turn, separate from visible activity. */
  thinking?: string;
  /** Current server-reported model phase, never private reasoning text. */
  activity?: AskActivity;
  /** The agent's tool-call trace for an assistant turn (what it searched/read). */
  toolCalls?: AskToolCall[];
  /** Wall-clock time the turn took, in ms (shown as "Completed in N sec"). */
  durationMs?: number;
  /** True while this assistant turn is still streaming in. */
  streaming?: boolean;
  /** Error text if this turn failed. */
  error?: string | null;
  /**
   * The member who wrote a user turn, for attribution in a team-shared thread
   * (resolved to a display name via {@link CampaignAskProps.resolveAuthorName}).
   * Null/undefined for an assistant turn (always hollie).
   */
  authorId?: string | null;
  /** ISO timestamp the turn was written, shown beside the author. */
  createdAt?: string;
}

/** A short, human label for one of the agent's tool calls. */
function toolLabel(call: AskToolCall): string {
  const labels: Record<string, [string, string, string]> = {
    search_conversations: ['Searching conversations', 'Searched conversations', 'Search failed'],
    read_conversation: ['Reading a conversation', 'Read a conversation', 'Read failed'],
    get_export_fields: ['Checking question filters', 'Checked question filters', 'Filter lookup failed'],
    export_campaign_csv: ['Preparing CSV export', 'Prepared CSV export', 'Export failed'],
    list_conversations: ['Checking conversations', 'Checked conversations', 'Conversation lookup failed'],
    list_questions: ['Checking questions', 'Checked questions', 'Question lookup failed'],
    summarize_answers: ['Analyzing answers', 'Analyzed answers', 'Answer analysis failed'],
    show_question_chart: ['Analyzing answers', 'Analyzed answers', 'Answer analysis failed'],
    compare_questions: ['Comparing answers', 'Compared answers', 'Comparison failed'],
  };
  const label = labels[call.name] ?? ['Running tool', 'Tool finished', 'Tool failed'];
  const text = label[call.ok === undefined ? 0 : call.ok ? 1 : 2];
  const query = call.name === 'search_conversations' && typeof call.args?.query === 'string' ? call.args.query : '';
  if (query && call.ok !== false) return `${call.ok ? 'Searched' : 'Searching'} "${query}"`;
  return text;
}

/** A tiny status dot for a tool call: volt when it succeeded, muted otherwise. */
const ToolDot: React.FC<{ ok?: boolean }> = ({ ok }) => (
  <span
    aria-hidden="true"
    className={`h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-200 motion-reduce:transition-none ${
      ok === false ? 'bg-danger' : ok ? 'bg-volt' : 'bg-muted-foreground/50'
    }`}
  />
);

/** Disclosure chevron for the activity summary (rotates when its details is open). */
const ChevronRight: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

/** Formats a turn duration as a compact "N sec" / "N min M sec" label. */
function formatDuration(ms: number): string {
  const totalSeconds = Math.max(1, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds} sec`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} sec`;
}

/**
 * Compact per-message timestamp: time of day (e.g. "3:45 PM"), prefixed with a
 * short date (e.g. "Jun 24, 3:45 PM") once the turn is older than today. Empty
 * string for a missing/invalid value so the caller can skip rendering it.
 */
function formatMessageTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const sameDay =
    d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  if (sameDay) return time;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

/** Split a display name into first / rest so {@link Avatar} can show initials. */
function splitName(name: string): { firstName: string; lastName: string } {
  const trimmed = name.trim();
  const space = trimmed.indexOf(' ');
  if (space === -1) return { firstName: trimmed, lastName: '' };
  return { firstName: trimmed.slice(0, space), lastName: trimmed.slice(space + 1) };
}

/** The single-line activity summary: "Working" while live, then "Completed in N sec". */
function activityLabel(message: ChatMessage): string {
  if (message.error) return 'Stopped';
  if (message.streaming) {
    const pending = message.toolCalls?.find(call => call.ok === undefined);
    if (pending) return toolLabel({ ...pending, args: undefined });
    if (message.activity === 'thinking') return 'Thinking';
    if (message.activity === 'reviewing') return 'Reviewing results';
    if (message.activity === 'answering') return 'Finishing';
    return 'Working';
  }
  if (message.durationMs != null) return `Completed in ${formatDuration(message.durationMs)}`;
  return 'Completed';
}

/** Reveal real work as it happens; keep a user's disclosure choice across updates. */
const AgentActivity: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const [choice, setChoice] = useState<boolean | null>(null);
  const expanded = choice ?? !!message.streaming;
  const tools = message.toolCalls ?? [];
  const pending = tools.some(call => call.ok === undefined);
  const detail = message.error ? 'Work stopped'
    : !message.streaming ? 'No tools used'
    : message.activity === 'thinking' ? 'Considering your question'
    : message.activity === 'reviewing' ? 'Reviewing the evidence'
    : message.activity === 'answering' ? 'Preparing the response'
    : 'Starting';
  return <div className="text-sm">
    <button type="button" aria-expanded={expanded} onClick={() => setChoice(!expanded)}
      className="flex w-fit items-center gap-1 text-left text-muted-foreground transition-colors hover:text-foreground">
      <ChevronRight className={`h-3 w-3 shrink-0 transition-transform duration-200 motion-reduce:transition-none ${expanded ? 'rotate-90' : ''}`} />
      <AnimatedLabel text={activityLabel(message)} />
    </button>
    <AnimatedPresence show={expanded}>
      <div role="status" aria-live="polite" className="flex flex-col gap-1 pt-2 pl-4">
        {tools.map((call, index) => <AnimatedPresence show key={call.id ?? index}>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <ToolDot ok={call.ok} />
            <AnimatedLabel text={toolLabel(call)} multiline className="min-w-0 flex-1 break-words text-foreground/75 [overflow-wrap:anywhere]" />
            <AnimatedPresence show={!!call.preview}>
              <span className="text-muted-foreground">{call.preview}</span>
            </AnimatedPresence>
          </div>
        </AnimatedPresence>)}
        <AnimatedPresence show={!pending && (!!message.streaming || tools.length === 0 || !!message.error)}>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full bg-volt ${message.streaming ? 'motion-safe:animate-pulse' : ''}`} />
            <AnimatedLabel text={detail} />
          </div>
        </AnimatedPresence>
      </div>
    </AnimatedPresence>
  </div>;
};

/** Props for {@link CampaignAsk}. */
export interface CampaignAskProps {
  /** Visible campaign used to bind saved export actions. */
  campaignId?: string;
  /** The chat thread, oldest first. */
  messages: ChatMessage[];
  /** Current composer text. Lifted to the parent so it survives remounts. */
  input: string;
  /** Called when the composer text changes. */
  onInputChange: (value: string) => void;
  /** Called to send the current composer text. */
  onSubmit: () => void;
  /** True while a turn is in flight (disables send, animates the orb). */
  busy: boolean;
  /** Called when a citation is clicked (best-effort deep link). */
  onCitationClick?: (citation: Citation) => void;
  /**
   * Resolve a user turn's `authorId` to a display name for attribution in a
   * team-shared thread. When omitted (or it returns empty), the user attribution
   * row is hidden and only the message bubble shows.
   */
  resolveAuthorName?: (authorId: string | null | undefined) => string;
  /** Composer placeholder. */
  placeholder?: string;
  /** Focus the composer on mount (used when the focused ask modal opens). */
  autoFocus?: boolean;
  /**
   * Fill the parent's height instead of growing with content. Used inside the
   * fixed-size modal: the thread region flexes to fill the space and scrolls,
   * the composer pins to the bottom, and an empty thread still reserves the
   * space so the surface never reflows. Requires a flex-column parent with a
   * definite height. Off by default (the surface grows with its content).
   */
  fill?: boolean;
  /**
   * Rendered in the thread region when there are no messages yet (e.g. a
   * greeting and recent conversations), so the empty surface is not just blank
   * space. Ignored once the thread has turns.
   */
  emptyState?: React.ReactNode;
}

/** How long the orb spends "looking" (eyes) before it switches to "thinking" (spin). */
const LOOKING_MS = 1400;

/** Matches a well-formed inline citation label, e.g. `[Cabc-123:T4]`. */
const LABEL_RE = /\[C([^:\]]+):T(\d+)\]/g;
/**
 * UUID shape of a real conversation id, so a `C<uuid>:T<n>` fragment is
 * unmistakably a citation label (never ordinary prose) even with no brackets.
 */
const UUID_SRC = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
/**
 * Catches malformed label-shaped residue between the well-formed labels so a raw
 * id / turn string can never leak into the prose. Three shapes, all keyed on the
 * `C<id>:T<digits>` signature:
 *  - bracketed (open `[`, optional close), e.g. `[Cconv-1:T3]` or a half-written one;
 *  - bracketless but keeping the close `]`, e.g. the model drops the `[`: `Cda113...:T10]`;
 *  - fully bracketless when the id is a real conversation UUID, e.g. `C639...85:T7`
 *    (the model sometimes emits the label with no brackets at all).
 * The non-UUID bracketless arm requires a bracket, so ordinary prose like a `C3:T4`
 * spreadsheet range or "Plan C: Type 3" is never matched.
 */
const RESIDUAL_LABEL_RE = new RegExp(
  '\\[C[^\\]]*?:\\s*T\\s*\\d+\\s*\\]?'
  + '|C[\\w-]+:\\s*T\\s*\\d+\\s*\\]'
  + `|C${UUID_SRC}:\\s*T\\s*\\d+\\s*\\]?`,
  'g',
);

/** A run of answer text, or a reference to a numbered citation. */
type AnswerPart = { text: string } | { ref: number; citation?: Citation };

/** Strip any malformed label residue from a plain-text run. */
function cleanResidual(text: string): string {
  return text.replace(RESIDUAL_LABEL_RE, '');
}

/**
 * Split an assistant answer into text runs and numbered citation references.
 *
 * Well-formed `[C<id>:T<idx>]` labels become small footnote numbers. Once
 * grounded citations are known, numbering follows their order and any label NOT
 * in the grounded set is dropped (so unverifiable refs never reach the reader).
 * While still streaming (no citations yet), labels are numbered by first
 * appearance so the superscripts are stable as text arrives. A trailing partial
 * label and any malformed label residue are stripped so no raw id ever shows.
 */
function buildParts(content: string, citations: Citation[], streaming: boolean): AnswerPart[] {
  let text = content;
  if (streaming) {
    text = text.replace(/\[C[^\]]*$/, '');
  }

  const grounded = citations.length > 0;
  const numberByKey = new Map<string, number>();
  if (grounded) {
    citations.forEach((c, i) => numberByKey.set(`${c.conversation_id}:${c.turn_index}`, i + 1));
  }

  const positional = new Map<string, number>();
  let nextPositional = 0;

  const parts: AnswerPart[] = [];
  let last = 0;
  for (const match of text.matchAll(LABEL_RE)) {
    const idx = match.index ?? 0;
    if (idx > last) {
      parts.push({ text: cleanResidual(text.slice(last, idx)) });
    }
    // Normalize the turn index (parse to int) so the key matches the server's
    // grounded-citation key even if a label is ever zero-padded (e.g. T04).
    const key = `${match[1]}:${Number.parseInt(match[2], 10)}`;
    let ref: number | undefined;
    let citation: Citation | undefined;
    if (grounded) {
      ref = numberByKey.get(key);
      if (ref !== undefined) {
        citation = citations[ref - 1];
      }
    } else {
      if (!positional.has(key)) {
        positional.set(key, ++nextPositional);
      }
      ref = positional.get(key);
    }
    if (ref !== undefined) {
      parts.push({ ref, citation });
    }
    last = idx + match[0].length;
  }
  if (last < text.length) {
    parts.push({ text: cleanResidual(text.slice(last)) });
  }
  return parts;
}

/** Renders a single assistant answer: prose with inline numbered citations. */
const AssistantAnswer: React.FC<{
  message: ChatMessage;
  campaignId?: string;
  onCitationClick?: (citation: Citation) => void;
}> = ({ message, onCitationClick, campaignId }) => {
  const parts = useMemo(
    () => buildParts(message.content, message.citations, !!message.streaming),
    [message.content, message.citations, message.streaming],
  );
  // Citation refs ride through the markdown as slots so they stay inline,
  // even inside a bold run or list item.
  const source = useMemo(
    () => parts.map((part, i) => ('text' in part ? stripSlotMarkers(part.text) : askSlot(i))).join(''),
    [parts],
  );

  const tools = message.toolCalls ?? [];
  const hasActivity = !!message.streaming || !!message.thinking || tools.length > 0 || message.durationMs != null;

  return (
    <div className="flex flex-col gap-2">
      {hasActivity && <AgentActivity message={message} />}

      <AskMarkdown
        source={source}
        className="text-base leading-relaxed text-foreground"
        renderSlot={i => {
          const part = parts[i];
          if (!part || 'text' in part) {
            return null;
          }
          return (
            <button
              type="button"
              onClick={() => part.citation && onCitationClick?.(part.citation)}
              disabled={!part.citation}
              className="mx-0.5 align-super text-[0.7em] font-semibold text-volt hover:underline disabled:no-underline"
            >
              [{part.ref}]
            </button>
          );
        }}
      />

      {campaignId && tools.filter(call => call.ok && call.name === 'export_campaign_csv' && call.csvExport).map((call, index) => <CampaignCsvExportCard key={call.id ?? index} value={call.csvExport} campaignId={campaignId} />)}

      {tools.some(call => call.ok && call.chart) && (
        <React.Suspense fallback={null}>
          {tools.filter(call => call.ok && call.chart).map((call, index) => <AskChartCard key={call.id ?? index} chart={call.chart!} />)}
        </React.Suspense>
      )}

      {message.citations.length > 0 && (
        <ol className="flex flex-col gap-1">
          {message.citations.map((citation, idx) => (
            <li key={`${citation.conversation_id}-${citation.turn_index}-${idx}`}>
              <button
                type="button"
                onClick={() => onCitationClick?.(citation)}
                className="flex w-full items-start gap-2 rounded-lg p-1.5 text-left transition-colors hover:bg-surface-hover"
              >
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-volt/15 text-[10px] font-semibold text-volt">
                  {idx + 1}
                </span>
                <span className="min-w-0 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{citation.respondent_name}</span>
                  {': '}
                  <span>{citation.quote}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}

      {message.error && <p className="text-sm text-danger">{message.error}</p>}
    </div>
  );
};

/** Send-arrow icon for the composer. */
const SendIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l14-7-7 14-2-5-5-2z" />
  </svg>
);

/** Microphone icon for voice dictation (matches the wizard composer). */
const MicIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
    />
  </svg>
);

/**
 * Ask-the-campaign chat surface. A living orb sits inside the question field (it
 * carries the agent's state, so no copy is needed: calm at rest, eyes while it
 * looks through the conversations, spin while it thinks). Answers stream in as a
 * thread above the composer and ground their claims with small numbered
 * citations that deep-link to the cited turn. The composer stays uncluttered:
 * the send affordance is a single icon that slides in only once there is
 * something to send.
 *
 * Presentational and stateless except the orb choreography; the thread, input,
 * and busy state are owned by the parent.
 */
export const CampaignAsk: React.FC<CampaignAskProps> = ({
  messages,
  campaignId,
  input,
  onInputChange,
  onSubmit,
  busy,
  onCitationClick,
  resolveAuthorName,
  placeholder = 'Ask hollie',
  autoFocus = false,
  fill = false,
  emptyState,
}) => {
  // Orb choreography driven purely by `busy`: looking -> thinking while in
  // flight, calm at rest.
  const [phase, setPhase] = useState<'idle' | 'looking' | 'thinking'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (busy) {
      setPhase('looking');
      timerRef.current = setTimeout(() => setPhase('thinking'), LOOKING_MS);
    } else {
      setPhase('idle');
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [busy]);
  const orbForm: OrbForm = phase === 'looking' ? 'eyes' : phase === 'thinking' ? 'spin' : 'orb';

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Focus the composer when the modal opens so the owner can type immediately.
  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);
  // Auto-grow the composer up to a cap so long questions stay readable.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [input]);

  // Follow the newest turn as it streams, but only when the user is already
  // near the bottom: scrolling up to re-read earlier turns must not be yanked
  // back down on every token. Guarded because not every environment (e.g.
  // jsdom) implements scrollIntoView.
  const threadRef = useRef<HTMLDivElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const end = threadEndRef.current;
    if (!end || typeof end.scrollIntoView !== 'function') return;
    const container = threadRef.current;
    if (container) {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom > 120) return; // user has scrolled up; leave them be
    }
    end.scrollIntoView({ block: 'end' });
  }, [messages]);

  const canSend = !busy && input.trim().length > 0;

  // Voice dictation: tap the mic to speak a question into the field. Hidden on
  // browsers without the Web Speech API so we never show a dead control.
  const { supported: micSupported, recording, toggle: toggleMic } = useSpeechDictation(
    input,
    onInputChange,
    'CampaignAsk.dictation',
    busy,
  );

  const handleSubmit = () => {
    if (!canSend) return;
    onSubmit();
  };

  return (
    <div className={`flex flex-col gap-4${fill ? ' min-h-0 flex-1' : ''}`}>
      {(fill || messages.length > 0) && (
        <div
          ref={threadRef}
          data-testid="ask-thread"
          className={`flex flex-col gap-4 overflow-y-auto pr-1 ${
            fill ? 'min-h-0 flex-1' : 'max-h-[60vh]'
          }`}
        >
          {messages.length === 0
            ? emptyState
            : messages.map((message) => {
                const time = formatMessageTime(message.createdAt);
                if (message.role === 'user') {
                  // Attribute the turn to its author (a thread is shared, so two
                  // user turns can be different teammates). The row is hidden when
                  // nothing can be attributed (no resolver and no timestamp).
                  const authorName = resolveAuthorName?.(message.authorId) ?? '';
                  const { firstName, lastName } = splitName(authorName);
                  return (
                    <div key={message.id} className="flex flex-col items-end gap-1">
                      {(authorName || time) && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          {authorName && <span className="font-medium text-foreground">{authorName}</span>}
                          {authorName && time && <span aria-hidden="true">·</span>}
                          {time && <time dateTime={message.createdAt}>{time}</time>}
                          {authorName && <Avatar size="xs" firstName={firstName} lastName={lastName} />}
                        </div>
                      )}
                      <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-volt/15 px-3 py-2 text-base text-foreground">
                        {message.content}
                      </p>
                    </div>
                  );
                }
                return (
                  <div key={message.id} className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <span
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 rounded-full bg-gradient-to-br from-volt to-volt-dark"
                      />
                      <span className="font-medium text-foreground">hollie</span>
                      {time && <span aria-hidden="true">·</span>}
                      {time && <time dateTime={message.createdAt}>{time}</time>}
                    </div>
                    <AssistantAnswer campaignId={campaignId} message={message} onCitationClick={onCitationClick} />
                  </div>
                );
              })}
          <div ref={threadEndRef} />
        </div>
      )}

      {/* Composer: the orb lives inside the field (no title, no helper copy);
          Enter sends; the send icon slides in only once there is something
          to send. */}
      <div className="relative shrink-0 rounded-2xl border border-border bg-surface focus-within:border-volt">
        <div className="pointer-events-none absolute left-2.5 top-2.5 h-9 w-9">
          <GlowingOrb state="listening" form={orbForm} className="h-full w-full" />
        </div>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          rows={1}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full resize-none bg-transparent py-3 pl-14 pr-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <div className="flex items-center justify-end px-2.5 pb-2 pt-1">
          {micSupported && (
            <button
              type="button"
              data-testid="ask-mic"
              aria-label={recording ? 'Stop recording' : 'Speak'}
              aria-pressed={recording}
              onClick={toggleMic}
              disabled={busy}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                recording
                  ? 'animate-pulse bg-danger/20 text-danger'
                  : 'text-muted-foreground hover:bg-surface-hover hover:text-foreground'
              }`}
            >
              <MicIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            aria-label="Send"
            aria-hidden={!canSend}
            tabIndex={canSend ? 0 : -1}
            onClick={handleSubmit}
            disabled={!canSend}
            className={`inline-flex h-8 items-center justify-center overflow-hidden rounded-full button-accent transition-all duration-300 ease-out ${
              canSend ? 'ml-1 w-8 opacity-100' : 'pointer-events-none ml-0 w-0 opacity-0'
            }`}
          >
            <SendIcon className="h-4 w-4 shrink-0" />
          </button>
        </div>
      </div>
    </div>
  );
};
